import React, { useState, useCallback, useRef, useEffect } from 'react';
import { UploadZone } from './UploadZone';
import { VideoPreview } from './VideoPreview';
import { Timeline, type TimelineHandle } from './Timeline';
import { EffectsPanel } from './EffectsPanel';
import { AddedEffectsPanel } from './AddedEffectsPanel';
import { ExportPanel } from './ExportPanel';
import { useVideo } from './hooks/useVideo';
import { EFFECT_DEFINITIONS, type VideoInfo, type EffectMarker } from './types';
import './styles.css';

export function AutoTemplate() {
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [markers, setMarkers] = useState<number[]>([]);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(null);
  const [effectMarkers, setEffectMarkers] = useState<EffectMarker[]>([]);
  const [selectedEffectId, setSelectedEffectId] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('video');
  const timelineRef = useRef<TimelineHandle>(null);

  const { videoRef, isPlaying, currentTime, seekTo, togglePlay, stepFrame } = useVideo(
    videoInfo?.duration || 0,
    videoInfo?.fps || 30
  );

  const handleUploaded = useCallback((info: VideoInfo) => {
    setVideoInfo(info);
    setMarkers(info.cuts);
    setFilename(info.filename);
  }, []);

  const handleReset = useCallback(() => {
    setVideoInfo(null);
    setMarkers([]);
    setSelectedMarkerIndex(null);
    setEffectMarkers([]);
    setSelectedEffectId(null);
    setFilename('video');
  }, []);

  const handleAddEffect = useCallback(
    (marker: Omit<EffectMarker, 'id'>) => {
      const newEffect: EffectMarker = {
        ...marker,
        id: crypto.randomUUID(),
      };
      setEffectMarkers((prev) => [...prev, newEffect]);
    },
    []
  );

  const handleRemoveEffect = useCallback((id: string) => {
    setEffectMarkers((prev) => prev.filter((e) => e.id !== id));
    setSelectedEffectId((prev) => (prev === id ? null : prev));
  }, []);

  const handleMoveEffect = useCallback((id: string, newTime: number) => {
    setEffectMarkers((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, time: Math.round(newTime * 100) / 100 } : m
      )
    );
  }, []);

  const handleSelectEffect = useCallback((id: string | null) => {
    setSelectedEffectId(id);
    if (id !== null) setSelectedMarkerIndex(null);
  }, []);

  const handleAddMarker = useCallback((time: number) => {
    setMarkers((prev) => {
      const rounded = Math.round(time * 100) / 100;
      if (prev.some((m) => Math.abs(m - rounded) < 0.01)) return prev;
      const newMarkers = [...prev, rounded].sort((a, b) => a - b);
      return newMarkers;
    });
  }, []);

  const handleRemoveMarker = useCallback((index: number) => {
    setMarkers((prev) => {
      const markerTime = prev[index];
      // Remove attached transitions
      if (markerTime !== undefined) {
        setEffectMarkers((prevEffects) =>
          prevEffects.filter((e) => !(e.category === 'transition' && Math.abs(e.time - markerTime) < 0.001))
        );
      }
      return prev.filter((_, i) => i !== index);
    });
    setSelectedMarkerIndex(null);
  }, []);

  const handleMoveMarker = useCallback((index: number, newTime: number) => {
    setMarkers((prev) => {
      const oldTime = prev[index];
      const rounded = Math.round(newTime * 100) / 100;
      if (index < 0 || index >= prev.length) return prev;
      
      // Move attached transitions along with the marker
      if (oldTime !== undefined && Math.abs(oldTime - rounded) > 0.001) {
        setEffectMarkers((prevEffects) =>
          prevEffects.map((e) =>
            e.category === 'transition' && Math.abs(e.time - oldTime) < 0.001
              ? { ...e, time: rounded }
              : e
          )
        );
      }
      
      const updated = [...prev];
      updated[index] = rounded;
      const sorted = updated.sort((a, b) => a - b);
      const newIndex = sorted.indexOf(rounded);
      setSelectedMarkerIndex((prevSelected) =>
        prevSelected === index ? newIndex : prevSelected
      );
      return sorted;
    });
  }, []);

  const handleSelectMarker = useCallback((index: number | null) => {
    setSelectedMarkerIndex(index);
    if (index !== null) setSelectedEffectId(null);
  }, []);

  const seekToNextMarker = useCallback(() => {
    const next = markers.find((m) => m > currentTime + 0.01);
    if (next !== undefined) seekTo(next);
  }, [markers, currentTime, seekTo]);

  const seekToPrevMarker = useCallback(() => {
    const prev = [...markers].reverse().find((m) => m < currentTime - 0.01);
    if (prev !== undefined) seekTo(prev);
  }, [markers, currentTime, seekTo]);

  // Hotkeys
  useEffect(() => {
    if (!videoInfo) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) seekToNextMarker();
          else stepFrame(1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) seekToPrevMarker();
          else stepFrame(-1);
          break;
        case 'KeyM':
          e.preventDefault();
          handleAddMarker(currentTime);
          break;
        case 'Equal':
        case 'NumpadAdd':
          e.preventDefault();
          timelineRef.current?.zoomIn();
          break;
        case 'Minus':
        case 'NumpadSubtract':
          e.preventDefault();
          timelineRef.current?.zoomOut();
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          if (selectedEffectId !== null) {
            handleRemoveEffect(selectedEffectId);
          } else if (selectedMarkerIndex !== null) {
            handleRemoveMarker(selectedMarkerIndex);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    videoInfo,
    togglePlay,
    stepFrame,
    seekToNextMarker,
    seekToPrevMarker,
    handleAddMarker,
    handleRemoveMarker,
    handleRemoveEffect,
    currentTime,
    selectedMarkerIndex,
    selectedEffectId,
  ]);

  if (!videoInfo) {
    return (
      <div className="at-container">
        <UploadZone onUploaded={handleUploaded} />
      </div>
    );
  }

  return (
    <div className="at-container">
      {/* Header with filename */}
      <div className="at-header">
        <div className="at-header-left">
          <button className="at-new-video-btn" onClick={handleReset}>
            Новое видео
          </button>
          <span className="at-filename" title={filename}>
            {filename}
          </span>
        </div>
        <div className="at-header-right">
          {markers.length} склеек / {effectMarkers.length} эфф.
        </div>
      </div>

      {/* Top section: Effects | Video | Added Effects */}
      <div className="at-top-section">
        {/* Left panel - Effects */}
        <div className="at-left-panel">
          <EffectsPanel
            currentTime={currentTime}
            markers={markers}
            selectedMarkerIndex={selectedMarkerIndex}
            onAddEffect={handleAddEffect}
          />
        </div>

        {/* Main content - Video only */}
        <div className="at-main-content">
          <VideoPreview
            videoRef={videoRef}
            videoId={videoInfo.id}
            currentTime={currentTime}
            duration={videoInfo.duration}
            isPlaying={isPlaying}
            fps={videoInfo.fps}
            onTogglePlay={togglePlay}
            filename={videoInfo.filename}
          />
        </div>

        {/* Right panel - Added Effects + Export */}
        <div className="at-right-panel">
          <AddedEffectsPanel
            effectMarkers={effectMarkers}
            selectedEffectId={selectedEffectId}
            onSelectEffect={handleSelectEffect}
            onRemoveEffect={handleRemoveEffect}
          />
          <ExportPanel
            markers={markers}
            effectMarkers={effectMarkers}
            duration={videoInfo.duration}
            videoId={videoInfo.id}
            filename={filename}
          />
        </div>
      </div>

      {/* Bottom section: Timeline full width */}
      <div className="at-timeline-section">
        <Timeline
          ref={timelineRef}
          duration={videoInfo.duration}
          currentTime={currentTime}
          markers={markers}
          selectedMarkerIndex={selectedMarkerIndex}
          onMarkerSelect={handleSelectMarker}
          onSeek={seekTo}
          onAddMarker={handleAddMarker}
          onRemoveMarker={handleRemoveMarker}
          onMoveMarker={handleMoveMarker}
          effectMarkers={effectMarkers}
          selectedEffectId={selectedEffectId}
          onSelectEffect={handleSelectEffect}
          onMoveEffect={handleMoveEffect}
          fps={videoInfo.fps}
          videoId={videoInfo.id}
        />
      </div>
    </div>
  );
}
